import React, { useEffect, useState } from 'react';
import {
    getUserExchanges, createUserExchange, updateUserExchange,
    deleteUserExchange, setPrimaryUserExchange, testUserExchange, selfTestUserExchange
} from '../../services/UserExchangesService';
import CredentialsGuide from '../../components/CredentialsGuide/CredentialsGuide';
import SharedExchangeSecurity from '../../components/CredentialsGuide/SharedExchangeSecurity';
import { EXCHANGE_GUIDES } from '../../data/setupGuides';
import { confirmDialog } from '../../utils/ConfirmDialog';

const EMPTY_FORM = {
    id: null,
    exchangeName: '',
    accessKey: '',
    secretKey: '',
    passphrase: '',
    testAccessKey: '',
    testSecretKey: '',
    testPassphrase: '',
    isPrimary: false,
    isActive: true,
    prodEnabled: true,
    testEnabled: false,
    // Snapshot do que o backend retorna pra mostrar status configurado/não
    // configurado nos badges (independe de digitar valor novo no input).
    hasProdConfigured: false,
    hasTestConfigured: false
};

const PASSPHRASE_EXCHANGES = ['OKX', 'BITGET'];

const TIER_LABEL = { stable: '✅ Consolidadas', certified: '🟦 Certificadas (CCXT)', experimental: '🧪 Experimentais' };
const TIER_ORDER = ['stable', 'certified', 'experimental'];
const KYC_LABEL = { required: 'Exige KYC', limited: 'Sem KYC até um limite', none: 'Sem KYC (DEX)', unknown: 'KYC a confirmar' };
const ORIGIN_LABEL = { yes: '🇧🇷 Opera no Brasil', no: '🌎 Exterior', unknown: '🌎 Exterior (a confirmar)' };

// Passa no filtro atual?
function matchesFilter(e, f) {
    if (f.q && !`${e.label} ${e.name}`.toLowerCase().includes(f.q.toLowerCase())) return false;
    if (f.recommended && !e.recommended) return false;
    if (f.market === 'spot' && !e.spot) return false;
    if (f.market === 'futures' && !e.futures) return false;
    if (f.launchpad && !e.launchpad) return false;
    if (f.origin === 'brazil' && e.brazil !== 'yes') return false;
    if (f.origin === 'foreign' && e.brazil === 'yes') return false;
    if (f.kyc === 'nokyc' && !(e.kyc === 'limited' || e.kyc === 'none')) return false;
    if (f.kyc === 'kyc' && e.kyc !== 'required') return false;
    return true;
}

// Rótulo compacto pra <option> (option não aceita HTML).
function optionLabel(e, already) {
    const caps = [e.spot && 'Spot', e.futures && 'Fut', e.launchpad && 'LP'].filter(Boolean).join('·');
    const kyc = e.kyc === 'required' ? 'KYC' : e.kyc === 'none' ? 'DEX' : e.kyc === 'limited' ? 'KYC-lim' : '';
    const bits = [caps, e.brazil === 'yes' ? '🇧🇷' : '🌎', kyc].filter(Boolean).join(' · ');
    return `${e.recommended ? '⭐ ' : ''}${e.label}${bits ? `  (${bits})` : ''}${e.beta ? ' · beta' : ''}${already ? ' · já cadastrada' : ''}`;
}

/**
 * Gerencia múltiplas corretoras configuradas pelo usuário. Substitui o
 * bloco single-exchange (creds prod) + bloco testnet (creds teste) do
 * Settings antigo.
 *
 * Modelo: uma é primary (a que o backend usa); outras ficam guardadas
 * pra troca rápida. Operação simultânea fica como projeto futuro.
 *
 * props:
 * - exchanges: metadata do backend (name, label) pra popular dropdown
 */
function ExchangesManager({ exchanges }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);
    const [error, setError] = useState('');
    const [testing, setTesting] = useState(null); // "${id}-${env}"
    const [testResult, setTestResult] = useState(null);
    const [rowTestResults, setRowTestResults] = useState({}); // key: "${id}-${env}"
    const [selfTesting, setSelfTesting] = useState(null);     // id em bateria
    const [selfTestResults, setSelfTestResults] = useState({}); // key: id → report
    // Filtros do seletor de corretora (são 100+ via CCXT).
    const [flt, setFlt] = useState({ q: '', recommended: false, market: 'all', launchpad: false, origin: 'all', kyc: 'all' });

    function load() {
        setLoading(true);
        getUserExchanges()
            .then(rows => { setList(rows || []); setLoading(false); })
            .catch(() => setLoading(false));
    }

    useEffect(() => { load(); }, []);

    function openNew() {
        const hasPrimary = list.some(e => e.isPrimary);
        setForm({ ...EMPTY_FORM, isPrimary: !hasPrimary });
        setError('');
    }

    function openEdit(row) {
        // secrets nunca voltam — só preenche se trocar
        setForm({
            ...EMPTY_FORM,
            id: row.id,
            exchangeName: row.exchangeName,
            isPrimary: row.isPrimary,
            isActive: row.isActive,
            prodEnabled: row.prodEnabled !== false,
            testEnabled: !!row.testEnabled,
            hasProdConfigured: !!row.accessKey,
            hasTestConfigured: !!row.testAccessKey
        });
        setError('');
        setTestResult(null);
    }

    function handleSave(e) {
        e.preventDefault();
        setError('');
        if (!form.exchangeName) return setError('Selecione a corretora.');
        if (!form.id) {
            if (!form.accessKey || !form.secretKey) return setError('accessKey e secretKey são obrigatórios.');
        }

        const payload = { ...form };
        // No PATCH não envia secret vazio (pra não sobrescrever existente)
        if (form.id) {
            ['accessKey', 'secretKey', 'passphrase', 'testAccessKey', 'testSecretKey', 'testPassphrase']
                .forEach(f => { if (!payload[f]) delete payload[f]; });
        }

        const promise = form.id ? updateUserExchange(form.id, payload) : createUserExchange(payload);
        promise
            .then(() => { setForm(null); load(); })
            .catch(err => setError(err.response?.data?.error || err.message || 'Erro ao salvar.'));
    }

    function handleSetPrimary(id) {
        setPrimaryUserExchange(id)
            .then(load)
            .catch(async err => {
                const data = err.response?.data;
                if (data?.error === 'open_positions') {
                    const ok = await confirmDialog({
                        title: 'Trocar corretora primary',
                        message: `${data.message}\n\nConfirma a troca?`,
                        confirmLabel: 'Trocar mesmo assim',
                        confirmVariant: 'warning'
                    });
                    if (ok) setPrimaryUserExchange(id, true).then(load).catch(e => setError(e.message));
                } else {
                    setError(data?.message || data?.error || err.message);
                }
            });
    }

    async function handleDelete(id, name) {
        const ok = await confirmDialog({
            title: `Remover ${name}`,
            message: `Tem certeza que quer remover esta corretora?\n\nAs credenciais (produção e testnet) serão apagadas permanentemente.`,
            confirmLabel: 'Remover',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        deleteUserExchange(id)
            .then(load)
            .catch(err => setError(err.response?.data?.error || err.message));
    }

    function handleToggleActive(row) {
        updateUserExchange(row.id, { isActive: !row.isActive }).then(load);
    }

    function handleToggleEnv(row, env) {
        const field = env === 'test' ? 'testEnabled' : 'prodEnabled';
        updateUserExchange(row.id, { [field]: !row[field] }).then(load);
    }

    function handleTest(row, environment = 'production') {
        setTesting(`${row.id}-${environment}`);
        setTestResult(null);
        testUserExchange(row.id, environment)
            .then(r => setTestResult({ ok: true, message: _coerceMessage(r?.message, 'Acesso OK.') }))
            .catch(err => {
                // Loga o erro bruto pra debug — útil quando a forma é
                // inesperada e a coerção precisa ser ajustada.
                // eslint-disable-next-line no-console
                console.error('[testUserExchange] erro bruto:', err, 'response:', err?.response);
                const raw = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? err;
                const message = _coerceMessage(raw, 'Falha no teste.');
                const detail = typeof raw === 'string' && raw !== message ? raw
                    : raw?.message && raw.message !== message ? raw.message
                    : null;
                setTestResult({ ok: false, message, detail });
            })
            .finally(() => setTesting(null));
    }

    function handleRowTest(rowId, environment) {
        const key = `${rowId}-${environment}`;
        setTesting(key);
        setRowTestResults(prev => { const n = { ...prev }; delete n[key]; return n; });
        testUserExchange(rowId, environment)
            .then(r => setRowTestResults(prev => ({ ...prev, [key]: { ok: true, message: _coerceMessage(r?.message, 'Acesso OK.') } })))
            .catch(err => {
                // eslint-disable-next-line no-console
                console.error('[testUserExchange] erro bruto:', err, 'response:', err?.response);
                const raw = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? err;
                const message = _coerceMessage(raw, 'Falha no teste.');
                const detail = typeof raw === 'string' && raw !== message ? raw
                    : raw?.message && raw.message !== message ? raw.message
                    : null;
                setRowTestResults(prev => ({ ...prev, [key]: { ok: false, message, detail } }));
            })
            .finally(() => setTesting(null));
    }

    // Bateria completa de API contra o testnet (place/status/cancel + fill real).
    function handleSelfTest(rowId) {
        setSelfTesting(rowId);
        setSelfTestResults(prev => { const n = { ...prev }; delete n[rowId]; return n; });
        selfTestUserExchange(rowId, { fill: true })
            .then(report => setSelfTestResults(prev => ({ ...prev, [rowId]: report })))
            .catch(err => {
                // eslint-disable-next-line no-console
                console.error('[selfTestUserExchange] erro bruto:', err, 'response:', err?.response);
                const raw = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? err;
                setSelfTestResults(prev => ({ ...prev, [rowId]: { ok: false, error: _coerceMessage(raw, 'Falha na bateria de testes.') } }));
            })
            .finally(() => setSelfTesting(null));
    }

    function _coerceMessage(v, fallback) {
        if (v == null) return fallback;
        if (typeof v === 'string') return v;
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
        // Pega o primeiro campo "humano" que for string
        const candidates = [v.msg, v.message, v.error, v.body, v.statusMessage, v.code];
        for (const c of candidates) {
            if (typeof c === 'string' && c) return c;
        }
        try { return JSON.stringify(v); } catch (_) { return fallback; }
    }

    async function handleRemoveTestCredentials() {
        if (!form.id) return;
        const ok = await confirmDialog({
            title: 'Remover credenciais de testnet',
            message: 'Tem certeza?\n\nAutomações em ambiente Teste passarão a simular ordens em memória (não vão hit no testnet real).',
            confirmLabel: 'Remover',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        updateUserExchange(form.id, { testAccessKey: null, testSecretKey: null, testPassphrase: null })
            .then(() => {
                setForm(prev => ({
                    ...prev,
                    testAccessKey: '', testSecretKey: '', testPassphrase: '',
                    hasTestConfigured: false
                }));
                setTestResult({ ok: true, message: 'Credenciais de testnet removidas.' });
                load();
            })
            .catch(err => setError(err.response?.data?.error || err.message || 'Falha ao remover.'));
    }

    async function handleRemoveProdCredentials() {
        if (!form.id) return;
        const isPrimary = form.isPrimary;
        const ok = await confirmDialog({
            title: isPrimary ? '⚠️ Remover credenciais da corretora PRIMARY' : 'Remover credenciais de produção',
            message: isPrimary
                ? 'Esta é a corretora PRIMARY.\n\nRemover as credenciais de produção vai PARAR todas as automações em ambiente Produção. Se houver posições abertas, elas continuam na corretora — você só perde o acesso pra gerenciar pelo bot.\n\nConfirma mesmo assim?'
                : 'Automações em ambiente Produção que dependem dela vão falhar até você cadastrar de novo.\n\nConfirma?',
            confirmLabel: 'Remover',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        updateUserExchange(form.id, { accessKey: null, secretKey: null, passphrase: null })
            .then(() => {
                setForm(prev => ({
                    ...prev,
                    accessKey: '', secretKey: '', passphrase: '',
                    hasProdConfigured: false
                }));
                setTestResult({ ok: true, message: 'Credenciais de produção removidas.' });
                load();
            })
            .catch(err => setError(err.response?.data?.error || err.message || 'Falha ao remover.'));
    }

    function renderCredCell(row, env) {
        const isConfigured = env === 'test' ? !!row.testAccessKey : !!row.accessKey;
        const isEnabled = env === 'test' ? row.testEnabled : row.prodEnabled;
        const key = `${row.id}-${env}`;
        const result = rowTestResults[key];
        if (!isConfigured) return <small className='text-muted'>—</small>;
        return (
            <div>
                <small className='text-success'>✓ configurado</small>
                {' '}
                <button type='button'
                    className={`btn py-0 px-1 ${isEnabled ? 'btn-success' : 'btn-outline-secondary'}`}
                    style={{ fontSize: 10 }}
                    title={isEnabled ? 'Ambiente ativo — clique para desativar' : 'Ambiente inativo — clique para ativar'}
                    onClick={() => handleToggleEnv(row, env)}>
                    {isEnabled ? '● ON' : '○ OFF'}
                </button>
                {' '}
                <button type='button' className='btn btn-outline-info py-0 px-1'
                    style={{ fontSize: 10 }} disabled={testing === key}
                    title='Teste rápido: autentica e lê a carteira (conectividade)'
                    onClick={() => handleRowTest(row.id, env)}>
                    {testing === key ? '...' : 'Testar Conexão'}
                </button>
                {env === 'test' && (
                    <>
                        {' '}
                        <button type='button' className='btn btn-outline-primary py-0 px-1'
                            style={{ fontSize: 10 }} disabled={selfTesting === row.id}
                            title='Bateria completa contra o testnet: dados de mercado, filtros, ciclo de ordem (place/status/cancel) e fill real — valida a normalização da API'
                            onClick={() => handleSelfTest(row.id)}>
                            {selfTesting === row.id ? 'testando API...' : 'Testar API'}
                        </button>
                    </>
                )}
                {result && (
                    <div style={{ fontSize: 11, marginTop: 2 }}>
                        <span className={result.ok ? 'text-success' : 'text-danger'}>
                            {result.ok ? '✓' : '✗'} {result.message}
                            {!result.ok && result.detail && (
                                <span title={result.detail} style={{ cursor: 'help', marginLeft: 4, opacity: 0.6 }}>ⓘ</span>
                            )}
                        </span>
                    </div>
                )}
                {env === 'test' && selfTestResults[row.id] && renderSelfTestReport(selfTestResults[row.id])}
            </div>
        );
    }

    // Relatório passo-a-passo da bateria "Testar API".
    function renderSelfTestReport(report) {
        if (report.error && !report.steps) {
            return <div style={{ fontSize: 11, marginTop: 3 }} className='text-danger'>✗ {report.error}</div>;
        }
        const passed = (report.steps || []).filter(s => s.ok).length;
        const total = (report.steps || []).length;
        return (
            <div className='border rounded p-2 mt-2' style={{ fontSize: 11, background: 'rgba(0,0,0,0.02)' }}>
                <div className='mb-1'>
                    <strong className={report.ok ? 'text-success' : 'text-danger'}>
                        {report.ok ? '✓ Bateria OK' : '✗ Bateria com falhas'}
                    </strong>{' '}
                    <span className='text-muted'>({passed}/{total} etapas · testnet)</span>
                </div>
                {(report.steps || []).map((s, i) => (
                    <div key={i} style={{ lineHeight: 1.4 }}>
                        <span>{s.ok ? '✅' : (s.soft ? '⚠️' : '❌')}</span>{' '}
                        <span className={s.ok ? '' : (s.soft ? 'text-warning' : 'text-danger')}>{s.name}</span>
                        {s.detail && <span className='text-muted'> — {s.detail}</span>}
                    </div>
                ))}
                {!report.ok && (
                    <div className='text-muted mt-1' style={{ fontSize: 10 }}>
                        ⚠️ = etapa opcional (ex.: fill exige saldo no testnet) — não reprova a integração.
                    </div>
                )}
            </div>
        );
    }

    if (loading) return <div className='text-muted py-2' style={{ fontSize: 14 }}>Carregando corretoras...</div>;

    // Exige passphrase? Deriva do metadata do CCXT (requiresPassphrase, ex.: KuCoin/OKX/Bitget);
    // fallback na lista fixa pras 4 à mão caso o metadata não venha.
    const _selMeta = form && (exchanges || []).find(e => e.name === form.exchangeName);
    const requiresPassphrase = !!(_selMeta && _selMeta.requiresPassphrase) || !!(form && PASSPHRASE_EXCHANGES.includes(form.exchangeName));

    return (
        <div className='mb-4'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
                <div>
                    <h5 className='mb-0'>💱 Minhas Corretoras</h5>
                    <small className='text-muted'>
                        Configure múltiplas corretoras: a <strong>primary</strong> é a que o bot opera no momento.
                        Operação simultânea entre corretoras é projeto futuro — por ora, troca-se entre elas com 1 clique.
                    </small>
                </div>
                <button className='btn btn-sm btn-primary' onClick={openNew}>+ Adicionar</button>
            </div>

            <SharedExchangeSecurity />

            {list.length === 0 && (
                <div className='alert alert-info py-2' style={{ fontSize: 13 }}>
                    Nenhuma corretora configurada. Adicione ao menos uma pra operar.
                </div>
            )}

            {list.length > 0 && (
                <div className='table-responsive'>
                    <table className='table table-sm table-hover align-middle mb-2'>
                        <thead className='thead-light'>
                            <tr>
                                <th>Corretora</th>
                                <th>Produção</th>
                                <th>Testnet</th>
                                <th>Papel</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(row => {
                                const meta = exchanges && exchanges.find(e => e.name === row.exchangeName);
                                return (
                                    <tr key={row.id} style={!row.isActive ? { opacity: 0.5 } : {}}>
                                        <td><strong>{meta ? meta.label : row.exchangeName}</strong></td>
                                        <td>{renderCredCell(row, 'production')}</td>
                                        <td>{renderCredCell(row, 'test')}</td>
                                        <td>
                                            {row.isPrimary
                                                ? <span className='badge bg-success'>🟢 Primary</span>
                                                : <span className='badge bg-secondary'>Secundária</span>}
                                        </td>
                                        <td>
                                            <button type='button'
                                                className={`btn btn-sm py-0 px-2 ${row.isActive ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                                                onClick={() => handleToggleActive(row)}
                                                title={row.isActive ? 'Clique para desativar' : 'Clique para ativar'}
                                                style={{ fontSize: 11 }}>
                                                {row.isActive ? 'Ativa' : 'Inativa'}
                                            </button>
                                        </td>
                                        <td className='text-end'>
                                            <div className='btn-group btn-group-sm' role='group'>
                                                {!row.isPrimary && (
                                                    <button type='button' className='btn btn-outline-success py-0 px-2'
                                                        title='Definir esta corretora como primary'
                                                        onClick={() => handleSetPrimary(row.id)} style={{ fontSize: 12 }}>
                                                        <svg width='12' height='12' fill='currentColor' viewBox='0 0 16 16' className='me-1' style={{ verticalAlign: '-2px' }}>
                                                            <path d='M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z'/>
                                                        </svg>
                                                        Tornar primary
                                                    </button>
                                                )}
                                                <button type='button' className='btn btn-outline-primary py-0 px-2'
                                                    title='Editar credenciais e configurações'
                                                    onClick={() => openEdit(row)} style={{ fontSize: 12 }}>
                                                    <svg width='12' height='12' fill='currentColor' viewBox='0 0 16 16' className='me-1' style={{ verticalAlign: '-2px' }}>
                                                        <path d='M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z'/>
                                                    </svg>
                                                    Editar
                                                </button>
                                                {!row.isPrimary && (
                                                    <button type='button' className='btn btn-outline-danger py-0 px-2'
                                                        title='Remover corretora e apagar credenciais'
                                                        onClick={() => handleDelete(row.id, meta ? meta.label : row.exchangeName)} style={{ fontSize: 12 }}>
                                                        <svg width='12' height='12' fill='currentColor' viewBox='0 0 16 16' className='me-1' style={{ verticalAlign: '-2px' }}>
                                                            <path d='M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z'/>
                                                            <path d='M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3h11V2h-11v1z'/>
                                                        </svg>
                                                        Remover
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {testResult && (
                <div className={`alert ${testResult.ok ? 'alert-success' : 'alert-danger'} py-2 mb-2 d-flex justify-content-between align-items-center`}
                     style={{ fontSize: 13 }}>
                    <span>
                        {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
                        {!testResult.ok && testResult.detail && (
                            <span title={testResult.detail} style={{ cursor: 'help', marginLeft: 6, opacity: 0.6, fontSize: 15 }}>ⓘ</span>
                        )}
                    </span>
                    <button className='btn-close' style={{ fontSize: 10 }} onClick={() => setTestResult(null)} />
                </div>
            )}

            {form && (
                <div className='card border-primary mt-3'>
                    <div className='card-body'>
                        <h6 className='mb-3'>{form.id ? `Editar corretora` : 'Nova corretora'}</h6>
                        {error && <div className='alert alert-danger py-1' style={{ fontSize: 13 }}>{error}</div>}

                        <div className='row'>
                            <div className='col-12 mb-2'>
                                <label className='form-label'>Corretora</label>

                                {/* Filtros (100+ corretoras via CCXT) — só na criação */}
                                {!form.id && (
                                    <div className='d-flex flex-wrap gap-2 mb-2 align-items-center'>
                                        <input type='text' className='form-control form-control-sm' style={{ maxWidth: 180 }}
                                            placeholder='🔎 buscar…' value={flt.q}
                                            onChange={e => setFlt({ ...flt, q: e.target.value })} />
                                        <select className='form-select form-select-sm' style={{ width: 'auto', minWidth: 150 }}
                                            value={flt.market} onChange={e => setFlt({ ...flt, market: e.target.value })}>
                                            <option value='all'>Mercado: todos</option>
                                            <option value='spot'>Spot</option>
                                            <option value='futures'>Futuros</option>
                                        </select>
                                        <select className='form-select form-select-sm' style={{ width: 'auto', minWidth: 150 }}
                                            value={flt.origin} onChange={e => setFlt({ ...flt, origin: e.target.value })}>
                                            <option value='all'>Origem: todas</option>
                                            <option value='foreign'>🌎 Exterior</option>
                                            <option value='brazil'>🇧🇷 Brasil</option>
                                        </select>
                                        <select className='form-select form-select-sm' style={{ width: 'auto', minWidth: 170 }}
                                            value={flt.kyc} onChange={e => setFlt({ ...flt, kyc: e.target.value })}>
                                            <option value='all'>KYC: todos</option>
                                            <option value='nokyc'>Sem KYC / limite</option>
                                            <option value='kyc'>Exige KYC</option>
                                        </select>
                                        <div className='form-check form-check-inline mb-0' style={{ fontSize: 12 }}>
                                            <input className='form-check-input' type='checkbox' id='flt-rec'
                                                checked={flt.recommended} onChange={e => setFlt({ ...flt, recommended: e.target.checked })} />
                                            <label className='form-check-label' htmlFor='flt-rec'>⭐ recomendadas</label>
                                        </div>
                                        <div className='form-check form-check-inline mb-0' style={{ fontSize: 12 }}>
                                            <input className='form-check-input' type='checkbox' id='flt-lp'
                                                checked={flt.launchpad} onChange={e => setFlt({ ...flt, launchpad: e.target.checked })} />
                                            <label className='form-check-label' htmlFor='flt-lp'>launchpad</label>
                                        </div>
                                    </div>
                                )}

                                <select className='form-select form-select-sm' value={form.exchangeName}
                                    onChange={e => setForm({ ...form, exchangeName: e.target.value })}
                                    disabled={!!form.id}>
                                    <option value=''>Selecionar...</option>
                                    {(() => {
                                        // Diferente da IA (múltiplas contas = capacidade no sharding), a corretora
                                        // opera UMA conta primária por vez → recadastrar a mesma daria 409. Então
                                        // marcamos e DESABILITAMOS as já cadastradas (na criação). Agrupa por tier.
                                        const visible = (exchanges || []).filter(e => matchesFilter(e, flt));
                                        return TIER_ORDER.map(tier => {
                                            const items = visible.filter(e => (e.tier || 'certified') === tier);
                                            if (!items.length) return null;
                                            return (
                                                <optgroup key={tier} label={TIER_LABEL[tier] || tier}>
                                                    {items.map(e => {
                                                        const already = list.some(r => r.exchangeName === e.name);
                                                        return (
                                                            <option key={e.name} value={e.name} disabled={already && !form.id}>
                                                                {optionLabel(e, already)}
                                                            </option>
                                                        );
                                                    })}
                                                </optgroup>
                                            );
                                        });
                                    })()}
                                </select>

                                {/* Painel da corretora selecionada: capacidades + KYC + origem + links */}
                                {(() => {
                                    const meta = (exchanges || []).find(e => e.name === form.exchangeName);
                                    if (!meta) return null;
                                    const badge = (txt, cls) => <span className={`badge ${cls} me-1`} style={{ fontSize: 10 }}>{txt}</span>;
                                    return (
                                        <div className='border rounded p-2 mt-2' style={{ fontSize: 12, background: 'rgba(0,0,0,0.02)' }}>
                                            <div className='mb-1'>
                                                {meta.spot && badge('Spot', 'bg-primary')}
                                                {meta.futures && badge('Futuros', 'bg-warning text-dark')}
                                                {meta.launchpad && badge('Launchpad', 'bg-info text-dark')}
                                                {meta.ws && badge('WebSocket', 'bg-secondary')}
                                                {badge(ORIGIN_LABEL[meta.brazil] || ORIGIN_LABEL.unknown, 'bg-light text-dark border')}
                                                {badge(KYC_LABEL[meta.kyc] || KYC_LABEL.unknown, meta.kyc === 'required' ? 'bg-dark' : 'bg-light text-dark border')}
                                            </div>
                                            {(meta.www || meta.apiDoc) && (
                                                <div className='mb-1'>
                                                    {meta.www && <a href={meta.www} target='_blank' rel='noreferrer' className='me-3'>🌐 Site oficial</a>}
                                                    {meta.apiDoc && <a href={meta.apiDoc} target='_blank' rel='noreferrer'>📄 Como obter chaves (doc da API)</a>}
                                                </div>
                                            )}
                                            {(meta.kyc === 'limited' || meta.brazil === 'unknown') && (
                                                <div className='text-muted' style={{ fontSize: 11 }}>
                                                    ℹ️ KYC/presença no Brasil são informações curadas e podem mudar — confirme no site oficial.
                                                </div>
                                            )}
                                            {meta.beta && (
                                                <div className='alert alert-warning py-1 px-2 mt-1 mb-0' style={{ fontSize: 12 }}>
                                                    ⚠️ {meta.betaNote || 'Integração nova — valide com "Testar API" (testnet) antes de operar com dinheiro real.'}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                {form.id && <small className='text-muted d-block mt-1'>Corretora não pode ser trocada depois de criada — exclua e recadastre.</small>}
                            </div>
                        </div>

                        {form.exchangeName && EXCHANGE_GUIDES[form.exchangeName] && (
                            <div className='row'>
                                <div className='col-12 mb-2'>
                                    <CredentialsGuide guide={EXCHANGE_GUIDES[form.exchangeName]} />
                                </div>
                            </div>
                        )}

                        {/* Card: Credenciais de Produção */}
                        <div className='card border-success mt-3'>
                            <div className='card-header bg-success bg-opacity-10 py-2 d-flex justify-content-between align-items-center'>
                                <div className='d-flex align-items-center gap-2'>
                                    <strong style={{ fontSize: 13 }}>🟢 Produção</strong>{' '}
                                    {form.hasProdConfigured
                                        ? <span className='badge bg-success ms-1' style={{ fontSize: 10 }}>✓ Configurado</span>
                                        : <span className='badge bg-secondary ms-1' style={{ fontSize: 10 }}>Não configurado</span>}
                                    {form.id && (
                                        <div className='form-check form-switch mb-0 ms-2' style={{ fontSize: 12 }}>
                                            <input className='form-check-input' type='checkbox' id='prodEnabled-form'
                                                checked={!!form.prodEnabled}
                                                onChange={e => setForm({ ...form, prodEnabled: e.target.checked })} />
                                            <label className='form-check-label' htmlFor='prodEnabled-form'>
                                                {form.prodEnabled ? 'Ativo' : 'Inativo'}
                                            </label>
                                        </div>
                                    )}
                                </div>
                                {form.id && form.hasProdConfigured && (
                                    <div>
                                        <button type='button' className='btn btn-sm btn-outline-info py-0 px-2 me-2'
                                            style={{ fontSize: 11 }}
                                            disabled={testing === `${form.id}-production`}
                                            onClick={() => handleTest({ id: form.id }, 'production')}>
                                            {testing === `${form.id}-production` ? 'testando...' : '🔌 Testar acesso'}
                                        </button>
                                        <button type='button' className='btn btn-sm btn-outline-danger py-0 px-2'
                                            style={{ fontSize: 11 }}
                                            onClick={handleRemoveProdCredentials}>
                                            🗑 Limpar
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className='card-body py-2'>
                                {form.id && form.hasProdConfigured && (
                                    <small className='d-block text-muted mb-2' style={{ fontSize: 12 }}>
                                        Preencha apenas se quiser <strong>substituir</strong> as credenciais salvas.
                                    </small>
                                )}
                                <div className='row'>
                                    <div className='col-md-6 mb-2'>
                                        <label className='form-label'>Access Key</label>
                                        <input type='text' className='form-control form-control-sm' value={form.accessKey}
                                            placeholder={form.hasProdConfigured ? '••••••••  (deixe em branco pra manter)' : 'Access Key'}
                                            onChange={e => setForm({ ...form, accessKey: e.target.value })} />
                                    </div>
                                    <div className='col-md-6 mb-2'>
                                        <label className='form-label'>Secret Key</label>
                                        <input type='password' className='form-control form-control-sm' value={form.secretKey}
                                            placeholder={form.hasProdConfigured ? '••••••••  (deixe em branco pra manter)' : 'Secret Key'}
                                            onChange={e => setForm({ ...form, secretKey: e.target.value })} />
                                    </div>
                                </div>
                                {requiresPassphrase && (
                                    <div className='row'>
                                        <div className='col-12 mb-2'>
                                            <label className='form-label'>Passphrase</label>
                                            <input type='password' className='form-control form-control-sm' value={form.passphrase}
                                                placeholder={form.hasProdConfigured ? '••••••••  (deixe em branco pra manter)' : 'Passphrase'}
                                                onChange={e => setForm({ ...form, passphrase: e.target.value })} />
                                            <small className='text-muted'>Obrigatório para {form.exchangeName}.</small>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Card: Credenciais de Testnet */}
                        <div className='card border-warning mt-3'>
                            <div className='card-header bg-warning bg-opacity-10 py-2 d-flex justify-content-between align-items-center'>
                                <div className='d-flex align-items-center gap-2'>
                                    <strong style={{ fontSize: 13 }}>🟡 Testnet</strong>{' '}
                                    {form.hasTestConfigured
                                        ? <span className='badge bg-success ms-1' style={{ fontSize: 10 }}>✓ Configurado</span>
                                        : <span className='badge bg-secondary ms-1' style={{ fontSize: 10 }}>Não configurado</span>}
                                    <span className='badge bg-warning text-dark ms-1' style={{ fontSize: 9 }}>OPCIONAL</span>
                                    {form.id && form.hasTestConfigured && (
                                        <div className='form-check form-switch mb-0 ms-2' style={{ fontSize: 12 }}>
                                            <input className='form-check-input' type='checkbox' id='testEnabled-form'
                                                checked={!!form.testEnabled}
                                                onChange={e => setForm({ ...form, testEnabled: e.target.checked })} />
                                            <label className='form-check-label' htmlFor='testEnabled-form'>
                                                {form.testEnabled ? 'Ativo' : 'Inativo'}
                                            </label>
                                        </div>
                                    )}
                                </div>
                                {form.id && form.hasTestConfigured && (
                                    <div>
                                        <button type='button' className='btn btn-sm btn-outline-info py-0 px-2 me-2'
                                            style={{ fontSize: 11 }}
                                            disabled={testing === `${form.id}-test`}
                                            onClick={() => handleTest({ id: form.id }, 'test')}>
                                            {testing === `${form.id}-test` ? 'testando...' : '🔌 Testar acesso'}
                                        </button>
                                        <button type='button' className='btn btn-sm btn-outline-danger py-0 px-2'
                                            style={{ fontSize: 11 }}
                                            onClick={handleRemoveTestCredentials}>
                                            🗑 Limpar
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className='card-body py-2'>
                                <small className='d-block text-muted mb-2' style={{ fontSize: 12 }}>
                                    {form.hasTestConfigured
                                        ? <>Preencha apenas se quiser <strong>substituir</strong> as credenciais salvas.</>
                                        : <>Em branco = automações em modo Teste vão simular ordens em memória, sem hit em testnet real.</>}
                                </small>
                                <div className='row'>
                                    <div className='col-md-6 mb-2'>
                                        <label className='form-label'>Test Access Key</label>
                                        <input type='text' className='form-control form-control-sm' value={form.testAccessKey}
                                            placeholder={form.hasTestConfigured ? '••••••••  (deixe em branco pra manter)' : 'Test Access Key'}
                                            onChange={e => setForm({ ...form, testAccessKey: e.target.value })} />
                                    </div>
                                    <div className='col-md-6 mb-2'>
                                        <label className='form-label'>Test Secret Key</label>
                                        <input type='password' className='form-control form-control-sm' value={form.testSecretKey}
                                            placeholder={form.hasTestConfigured ? '••••••••  (deixe em branco pra manter)' : 'Test Secret Key'}
                                            onChange={e => setForm({ ...form, testSecretKey: e.target.value })} />
                                    </div>
                                </div>
                                {requiresPassphrase && (
                                    <div className='row'>
                                        <div className='col-12 mb-2'>
                                            <label className='form-label'>Test Passphrase</label>
                                            <input type='password' className='form-control form-control-sm' value={form.testPassphrase}
                                                placeholder={form.hasTestConfigured ? '••••••••  (deixe em branco pra manter)' : 'Test Passphrase'}
                                                onChange={e => setForm({ ...form, testPassphrase: e.target.value })} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className='row mt-2'>
                            <div className='col-12 mb-2'>
                                <div className='form-check'>
                                    <input className='form-check-input' type='checkbox' id='isPrimary-form'
                                        checked={!!form.isPrimary}
                                        onChange={e => setForm({ ...form, isPrimary: e.target.checked })}
                                        disabled={!!form.id} />
                                    <label className='form-check-label' htmlFor='isPrimary-form'>
                                        Definir como corretora <strong>primary</strong> (a que o bot opera agora)
                                    </label>
                                    {form.id && <small className='d-block text-muted'>Use o botão "Tornar primary" na tabela acima.</small>}
                                </div>
                            </div>
                        </div>

                        <div className='d-flex justify-content-end gap-2 mt-2'>
                            <button type='button' className='btn btn-sm btn-secondary' onClick={() => setForm(null)}>Cancelar</button>
                            <button type='button' className='btn btn-sm btn-primary' onClick={handleSave}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ExchangesManager;
